/**
 * Test mode's stand-in for a placed object, as pure functions: the same
 * handles a placed object gets in a project (the run's start/end handles and
 * the type's depth handles for LINE objects; the gizmo scales width/depth for
 * all), each change followed by the object's sync cascade (`settleSync`) —
 * so steps ↔ count, the linked depth and handle drags behave exactly as they
 * will in the app.
 */

import type { ObjectDefinitionSpec, ObjectParamValue } from 'shared-types';
import { customHandleLayout } from '../../../potree/sketchObjects/custom/customSketchObjectType';
import { depthHandleDrag, settleSync, type LiveObjectState } from '../../../potree/sketchObjects/custom/customSync';
import { resolveCustomParams } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import type { SketchObjectFrame } from '../../../potree/sketchObjects/types';
import { testSize, type TestInstance } from '../EditorContext';

/** Smallest run / depth a drag leaves (the app's MIN_RUN_LENGTH). */
export const MIN_TEST_RUN = 0.05;

export const testCenter = (test: TestInstance): readonly [number, number] => test.center ?? [0, 0];

/** The test object's frame on the canvas (+u right, +n up, centered on its center). */
export function testFrame(spec: ObjectDefinitionSpec, test: TestInstance): SketchObjectFrame {
  const { width, depth } = testSize(test, spec);
  const [cu, cn] = testCenter(test);
  return { center: { x: cu, y: cn }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width, depth, z: 0 };
}

export function testLive(spec: ObjectDefinitionSpec, test: TestInstance): LiveObjectState {
  const { width, depth } = testSize(test, spec);
  return { width, depth, params: resolveCustomParams(spec, test.params) };
}

// Merged, not replaced: values that are not the spec's own params (a dimension's offset) survive a resize.
const withLive = (test: TestInstance, live: LiveObjectState): TestInstance => ({ ...test, width: live.width, depth: live.depth, params: { ...test.params, ...live.params } });

/** A property of the test object changed (panel edit): the new value, then the sync cascade. */
export function setTestValue(spec: ObjectDefinitionSpec, test: TestInstance, key: string, value: ObjectParamValue): TestInstance {
  const live = testLive(spec, test);
  const next: LiveObjectState =
    key === 'width' && typeof value === 'number' ? { ...live, width: value } : key === 'depth' && typeof value === 'number' ? { ...live, depth: value } : { ...live, params: { ...live.params, [key]: value } };
  return withLive(test, settleSync(spec, next, key));
}

export type TestHandle = { kind: 'start' | 'end'; u: number; n: number } | { kind: 'depth'; index: number; u: number; n: number; side: 1 | -1 };

/** The resize handles a placed object shows (canvas coordinates): LINE runs only, like the app. */
export function testHandles(spec: ObjectDefinitionSpec, test: TestInstance): TestHandle[] {
  if (spec.anchor !== 'LINE') return [];
  const frame = testFrame(spec, test);
  const [cu, cn] = testCenter(test);
  // The app honors a custom layout on FREE runs only (see SelectedObjectDrag).
  const layout = spec.constraint.kind === 'FREE' ? customHandleLayout('test', spec)?.(frame, testLive(spec, test).params) : undefined;
  const start = layout?.start ?? [-frame.width / 2, 0];
  const end = layout?.end ?? [frame.width / 2, 0];
  return [
    { kind: 'start', u: cu + start[0], n: cn + start[1] },
    { kind: 'end', u: cu + end[0], n: cn + end[1] },
    ...(layout?.depth ?? []).map((d, index): TestHandle => ({ kind: 'depth', index, u: cu + d.u, n: cn + d.n, side: d.side })),
  ];
}

/** Drag a run end to `cursorU` (canvas u): the other end stays put, the width follows (then the sync). */
export function dragTestRunEnd(spec: ObjectDefinitionSpec, before: TestInstance, end: 'start' | 'end', cursorU: number): TestInstance {
  const { width } = testSize(before, spec);
  const [cu, cn] = testCenter(before);
  const u0 = cu - width / 2;
  const u1 = cu + width / 2;
  const [a, b] = end === 'end' ? [u0, Math.max(u0 + MIN_TEST_RUN, cursorU)] : [Math.min(u1 - MIN_TEST_RUN, cursorU), u1];
  const settled = settleSync(spec, { ...testLive(spec, before), width: b - a }, 'width');
  return { ...withLive(before, settled), center: [(a + b) / 2, cn] };
}

/**
 * Drag depth handle `index` to `cursorN` (canvas n): the opposite edge stays
 * put; a handle with `assign` sets params from the pulled distance and the
 * depth follows the link — the app's SelectedObjectDrag + custom `apply`.
 */
export function dragTestDepthHandle(spec: ObjectDefinitionSpec, before: TestInstance, index: number, cursorN: number): TestInstance {
  const handle = spec.handles?.depth?.[index];
  if (!handle) return before;
  const { depth } = testSize(before, spec);
  const [cu, cn] = testCenter(before);
  const side = handle.side;
  const pinnedN = cn - (side * depth) / 2;
  const pulled = Math.max(MIN_TEST_RUN, (cursorN - cn) * side + depth / 2);
  let live = testLive(spec, before);
  let newDepth = pulled;
  if (handle.assign) {
    const drag = depthHandleDrag(spec, handle.assign, live, pulled);
    for (const w of drag.writes) live = settleSync(spec, { ...live, params: { ...live.params, [w.key]: w.value } }, w.key);
    newDepth = drag.depth;
  }
  newDepth = Math.max(MIN_TEST_RUN, newDepth);
  live = settleSync(spec, { ...live, depth: newDepth }, 'depth');
  return { ...withLive(before, live), center: [cu, pinnedN + (side * newDepth) / 2] };
}

/** The gizmo's scale on the test object: width/depth about the center (then the sync). */
export function scaleTestInstance(spec: ObjectDefinitionSpec, before: TestInstance, axes: readonly ('x' | 'y' | 'z')[], factor: number): TestInstance {
  const size = testSize(before, spec);
  let live = testLive(spec, before);
  if (axes.includes('x')) live = settleSync(spec, { ...live, width: Math.max(MIN_TEST_RUN, size.width * factor) }, 'width');
  if (axes.includes('y')) live = settleSync(spec, { ...live, depth: Math.max(MIN_TEST_RUN, size.depth * factor) }, 'depth');
  return withLive(before, live);
}
